/**
 * The study hub's compact ad-spend section. Plan §7.5.
 *
 * A SUMMARY, not the report. Campaign level only, no expansion chevrons, four
 * columns — the full campaign → ad set → ad tree lives in Reports and this
 * links to it rather than duplicating it.
 *
 * TWO THINGS IT MUST NOT DO, both of which a naive version would:
 *  1. Hide itself when nothing maps. A study whose ads are unmapped has to LOOK
 *     unmapped, not unadvertised — otherwise "no ad spend" and "nobody told the
 *     system which campaign this was" are indistinguishable, and the second one
 *     is fixable in thirty seconds.
 *  2. Present a campaign's whole spend as this study's cost without saying so.
 *     A campaign that also served other studies is charged whole to whichever
 *     study it maps to (§2.2 — spend is never prorated), so the amber note is
 *     what keeps that visible rather than quietly overstating this study's cost.
 *
 * Admin-gated in the component because PerformanceTab itself is not admin-only.
 * The endpoint's 403 is the real gate; this only keeps the section from
 * rendering an error box for every recruiter.
 *
 * It renders its OWN <section> and heading rather than sitting inside the tab's
 * Section wrapper. If the parent owned the heading, a non-admin — or anyone
 * with the feature switched off — would get an "Ad spend" title with nothing
 * underneath it, which is exactly the blank card §7.5 forbids.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { useIsCRMAdmin } from '../../lib';
import { fmtCurrency } from './StatBlocks';
import type { AdRow } from './AdSpendReport';

/** Hoisted deliberately: declared inside the component it would be a NEW
 *  component type on every render, so React would unmount and remount the whole
 *  table each time. */
function Wrap({ children }: { children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Ad spend</h3>
      {children}
    </section>
  );
}

export default function AdSpendStudySection({
  studyId, from, to,
}: { studyId: string; from: string | null; to: string | null }) {
  const isAdmin = useIsCRMAdmin();
  const [rows, setRows] = useState<AdRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    setLoading(true);
    const p = new URLSearchParams();
    p.set('level', 'campaign');
    p.set('study_id', studyId);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    api
      .get(`/api/crm/analytics/ad-spend/performance?${p.toString()}`)
      .then((res) => { if (alive) { setRows(res.data?.rows || []); setUnavailable(false); } })
      .catch(() => { if (alive) setUnavailable(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [studyId, from, to, isAdmin]);

  // Nothing at all for a non-admin or a switched-off feature: no heading, no box.
  if (!isAdmin || unavailable) return null;

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      </Wrap>
    );
  }

  const detailLink = (
    <Link
      to={`/crm/reports?tab=adspend&study=${encodeURIComponent(studyId)}`}
      className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
    >
      See campaign, ad set and ad detail
      <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );

  if (!rows.length) {
    // Explicit, never a blank card and never a hidden section.
    return (
      <Wrap>
      <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-600">
        <p className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
          No ad campaigns are mapped to this study yet
        </p>
        <p className="mx-auto mb-3 max-w-md text-sm text-gray-500 dark:text-gray-400">
          Spend cannot be attributed to a study until someone says which campaigns were
          selling it. That is one dropdown per campaign.
        </p>
        <Link
          to="/crm/settings/ad-campaigns"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
        >
          Map a campaign to this study
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      </Wrap>
    );
  }

  const shared = rows.filter((r) => (r.study_mix?.length || 0) > 1);

  return (
    <Wrap>
    <div className="space-y-2">
      {shared.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {shared.length === 1 ? 'One campaign below also' : `${shared.length} campaigns below also`} produced
            leads for other studies. Meta charges the campaign, not the study, so its whole
            spend is counted here — this study&rsquo;s true cost is lower.
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Campaign</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Spend</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Randomized</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Cost / randomized</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((r) => {
                const n = Number(r.randomized || 0);
                return (
                  <tr key={r.object_id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      <span className="inline-flex items-center gap-1.5">
                        {(r.study_mix?.length || 0) > 1 && (
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" aria-label="Also served other studies" />
                        )}
                        {r.object_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                      {/* Same rule as the full report: since 20260818150000 a
                          null spend means no crm.ad_spend_daily row exists for
                          this campaign in this window at all, which is not the
                          claim "$0.00 was spent". A measured 0.00 still prints. */}
                      {r.spend === null || r.spend === undefined ? (
                        <span
                          className="text-gray-400 dark:text-gray-500"
                          title="No spend has been imported for this campaign in this date range — which is not the same as having spent nothing."
                        >
                          —
                        </span>
                      ) : (
                        fmtCurrency(Number(r.spend), r.currency)
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                      {n.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                      {r.cost_per_randomized == null || n <= 0 ? (
                        <span className="text-gray-400 dark:text-gray-500" title="No randomizations yet — a cost per randomized cannot be computed">—</span>
                      ) : (
                        <span className="font-medium">{fmtCurrency(Number(r.cost_per_randomized), r.currency)}</span>
                      )}
                      <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">n={n.toLocaleString()}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">{detailLink}</div>
    </div>
    </Wrap>
  );
}
